// Undo and rewind (issue 1169), as the DM's screen asks for them: the scene's recent moments and Undo's change, read
// from `/api/tables/:campaignId/rewind` (the DM's alone), and Undo kept current a beat after anything on the table moves.
// The rewind itself goes over the socket like any act; nothing here computes what it restores.
import { useEffect, useState } from 'react';
import { requestJson } from './api';

/** A moment: the scene just before a change, said as the history says it. `to` is handed back as it came. */
/** `whoId` is who made the change (an older server sends none), for the table's name for them. */
export interface Moment { to: string; at: string; who: string | null; whoId?: string | null; summary: string }
export interface Moments { moments: Moment[]; undo: Moment | null }

const isMoment = (v: unknown): v is Moment => {
  const m = v as Partial<Moment> | null;
  return !!m && typeof m.to === 'string' && typeof m.at === 'string' && typeof m.summary === 'string' && (m.who === null || typeof m.who === 'string');
};

/** The scene's moments, newest first, and Undo's; null when they cannot be read. */
export async function fetchMoments(campaignId: string, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<Moments | null> {
  const q = sceneId ? `?scene=${encodeURIComponent(sceneId)}` : '';
  const { status, body } = await requestJson(`/api/tables/${campaignId}/rewind${q}`, { method: 'GET' }, fetchImpl);
  if (status !== 200 || typeof body !== 'object' || body === null) return null;
  const b = body as { moments?: unknown; undo?: unknown };
  return { moments: Array.isArray(b.moments) ? b.moments.filter(isMoment) : [], undo: isMoment(b.undo) ? b.undo : null };
}

/** How long after the last change Undo is read again: one read for a burst. */
export const UNDO_BEAT_MS = 300;

/**
 * Undo's change for the DM's screen: read on arrival and a beat after every change on the scene (`tick` moves with
 * the store's counter). Null when there is none, or for anyone but the DM (`enabled`).
 */
export function useUndo(campaignId: string, sceneId: string | null, tick: number, enabled: boolean, fetchImpl: typeof fetch = fetch): Moment | null {
  const [undo, setUndo] = useState<Moment | null>(null);
  useEffect(() => {
    if (!enabled) { setUndo(null); return undefined; }
    let live = true;
    const t = setTimeout(() => { void fetchMoments(campaignId, sceneId, fetchImpl).then((m) => { if (live) setUndo(m?.undo ?? null); }); }, UNDO_BEAT_MS);
    return () => { live = false; clearTimeout(t); };
  }, [campaignId, sceneId, tick, enabled, fetchImpl]);
  return undo;
}

/** A moment's time as the dialog shows it: the hour and minute, to the second. */
export const momentTime = (at: string): string => new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * A moment's day, when it is not today (issue 1450): "yesterday", or the weekday and date ("Tue 13 Oct"), in this
 * browser's own calendar. Null for today. The list reaches back days, so a time alone read as out of order.
 */
export function momentDay(at: string, now: Date = new Date()): string | null {
  const d = new Date(at);
  const dayOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const back = Math.round((dayOf(now) - dayOf(d)) / 86_400_000);
  if (back === 0) return null;
  if (back === 1) return 'yesterday';
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

/** A moment's day and time for the list ("Yesterday 12:08:39 PM"), or its time alone today. */
export function momentWhen(at: string, now?: Date): string {
  const day = momentDay(at, now);
  return day ? `${day[0]!.toUpperCase()}${day.slice(1)} ${momentTime(at)}` : momentTime(at);
}
